import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderHeart, FileQuestion, Calendar, FileSignature,
} from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import localApiAdapter from '../../core/services/localApiAdapter';
import { normalizeDashboardPayload } from '../../shared/components/dashboard/dashboardPayloadNormalize';
import { buildDashboardCharts, pipelineFromKpis } from '../../shared/components/dashboard/moduleDashboardCharts';
import { usePortalEmployeeContext } from './PortalEmployeeContext';
import { ALL_HR_DATA_REFRESH_EVENTS, useHrApiRefreshListener } from '../ressources-humaines/hrRefreshEvents';
import { computeSaudiLeaveBalances, EmployeePortalScopeAlert } from './employeePortalShared';

export default function EmployeePortalAnalysesPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.employee_portal.pages.employeePortalAnalysesPage.${key}`, opts);
  const { email, employeeId, profileResolved, scopeReady } = usePortalEmployeeContext();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (employeeId) params.employee_id = employeeId;
      else if (email) params.email = email;

      const dashRes = await localApiAdapter.get('/api/employee-portal/dashboard', { params });
      setData(dashRes?.data ?? dashRes);
    } catch (error) {
      console.error('[EmployeePortalAnalyses] load error:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [email, employeeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useHrApiRefreshListener(ALL_HR_DATA_REFRESH_EVENTS, loadData);

  const portalStats = data?.stats || {};
  const { kpis, chartRows } = useMemo(
    () => normalizeDashboardPayload('/employee-portal/dashboard', data || {}),
    [data],
  );
  const leaveBalances = useMemo(() => {
    if (data?.leaveBalances) return data.leaveBalances;
    return computeSaudiLeaveBalances(data?.recentLeaves || []);
  }, [data?.leaveBalances, data?.recentLeaves]);

  const annualRemaining = num(leaveBalances.annual?.remaining ?? leaveBalances.annualRemaining);
  const sickRemaining = num(leaveBalances.sick?.remaining ?? leaveBalances.sickRemaining);
  const pendingRequests = num(kpis.pendingRequests ?? portalStats.pendingRequests);
  const pendingLeaves = num(portalStats.pendingLeaves);
  const completedRequests = useMemo(
    () => resolveCompletedRequests(portalStats, chartRows, data?.recentRequests),
    [portalStats, chartRows, data?.recentRequests],
  );

  const stats = useMemo(
    () => sliceQawaemStats([
      {
        label: p('stat_documents'),
        value: portalStats.documents ?? 0,
        icon: FolderHeart,
      },
      {
        label: p('stat_pending_requests'),
        value: portalStats.pendingRequests ?? 0,
        icon: FileQuestion,
      },
      {
        label: p('stat_pending_leaves'),
        value: portalStats.pendingLeaves ?? 0,
        icon: Calendar,
      },
      {
        label: p('stat_active_contracts'),
        value: portalStats.activeContracts ?? 0,
        icon: FileSignature,
      },
    ], 4),
    [portalStats, p]
  );

  const insights = useMemo(() => {
    const totalLeave = num(kpis.leaveBalance) || annualRemaining + sickRemaining;
    const pendingTotal = pendingRequests + pendingLeaves;
    const items = [
      {
        label: p('insight_leave_balance'),
        value: `${totalLeave} ${p('days_unit')}`,
        hint: (annualRemaining || sickRemaining)
          ? p('insight_leave_hint', { annual: annualRemaining, sick: sickRemaining })
          : undefined,
      },
      {
        label: p('insight_pending_total'),
        value: pendingTotal,
        hint: p('insight_pending_hint'),
      },
    ];
    if (kpis.trainingHours != null) {
      items.push({
        label: p('insight_training_hours'),
        value: `${num(kpis.trainingHours)} ${p('hours_unit')}`,
      });
    }
    return items;
  }, [kpis, annualRemaining, sickRemaining, pendingRequests, pendingLeaves, p]);

  const charts = useMemo(
    () => buildDashboardCharts({
      pipeline: {
        title: p('chart_leave_balances'),
        subtitle: p('chart_leave_subtitle'),
        alwaysShow: true,
        rows: pipelineFromKpis([
          { label: p('leave_annual'), value: annualRemaining },
          { label: p('leave_sick'), value: sickRemaining },
        ]),
      },
      status: {
        title: p('chart_requests_status'),
        subtitle: p('chart_requests_status_subtitle'),
        active: completedRequests,
        inactive: pendingRequests,
        alwaysShow: true,
      },
      ranked: {
        title: p('chart_activity_ranked'),
        subtitle: p('chart_activity_subtitle'),
        rows: chartRows.length
          ? chartRows
          : pipelineFromKpis([
            { label: p('stat_pending_requests'), value: pendingRequests },
            { label: p('stat_pending_leaves'), value: pendingLeaves },
            { label: p('stat_announcements'), value: num(portalStats.publishedAnnouncements) },
          ]),
        alwaysShow: true,
      },
    }),
    [
      annualRemaining,
      sickRemaining,
      completedRequests,
      pendingRequests,
      pendingLeaves,
      chartRows,
      portalStats.publishedAnnouncements,
      p,
    ],
  );

  const table = useMemo(() => {
    const recentLeaves = data?.recentLeaves || [];
    if (recentLeaves.length) {
      return {
        title: p('table_recent_leaves'),
        columns: [
          { key: 'type', label: p('col_type') },
          { key: 'start', label: p('col_start') },
          { key: 'end', label: p('col_end') },
          { key: 'status', label: p('col_status') },
        ],
        rows: recentLeaves.slice(0, 10).map((row) => ({
          type: row.type || row.leave_type || '—',
          start: row.start_date || row.startDate || '—',
          end: row.end_date || row.endDate || '—',
          status: row.status || '—',
        })),
      };
    }
    return {
      title: p('table_summary'),
      columns: [
        { key: 'metric', label: p('col_metric') },
        { key: 'value', label: p('col_value') },
      ],
      rows: [
        { metric: p('stat_documents'), value: portalStats.documents ?? 0 },
        { metric: p('stat_pending_requests'), value: portalStats.pendingRequests ?? 0 },
        { metric: p('stat_announcements'), value: portalStats.publishedAnnouncements ?? 0 },
        { metric: p('stat_pending_leaves'), value: portalStats.pendingLeaves ?? 0 },
        { metric: p('stat_active_contracts'), value: portalStats.activeContracts ?? 0 },
        {
          metric: p('leave_annual'),
          value: `${num(leaveBalances.annual?.remaining ?? leaveBalances.annualRemaining)} ${p('days_unit')}`,
        },
        {
          metric: p('leave_sick'),
          value: `${num(leaveBalances.sick?.remaining ?? leaveBalances.sickRemaining)} ${p('days_unit')}`,
        },
      ],
    };
  }, [data, portalStats, leaveBalances, p]);

  return (
    <>
      {profileResolved && !scopeReady ? (
        <EmployeePortalScopeAlert />
      ) : null}
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={loadData}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="employee_portal_analyses"
      emptyLabel={p('empty')}
    />
    </>
  );
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function resolveCompletedRequests(portalStats, chartRows, recentRequests) {
  if (portalStats.completedRequests != null) return num(portalStats.completedRequests);
  const fromRow = (chartRows || []).find((row) => /مكتمل|completed/i.test(String(row.label || '')));
  if (fromRow) return num(fromRow.value);
  if (Array.isArray(recentRequests)) {
    return recentRequests.filter((row) => {
      const status = String(row.status || '').toLowerCase();
      return status === 'completed' || status === 'traite' || status === 'done';
    }).length;
  }
  return 0;
}
